"use client";

import { memo, useEffect, useRef, useState, useCallback } from "react";
import Link from "@/components/ui/Link";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import Image from "next/image";
// import { useTheme } from "next-themes";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";
import { getMenuGroups, MenuGroupItem } from "@/lib/api";
import { FaEllipsisH, FaChevronDown, FaSearch, FaUser, FaTimes, FaBookmark } from "react-icons/fa";
import { HiMenu } from "react-icons/hi";
import SocialIcons from "./SocialIcons";
import { useDevice } from '@/contexts/DeviceContext';

// Lazy load heavy components (only needed on interaction)
const MegaMenu = dynamic(() => import("./MegaMenu"), {
  ssr: false
});
const SearchLayoutSection = dynamic(() => import("./SearchLayoutSection"), {
  ssr: false
});
const LoginRegisterForm = dynamic(() => import("../component_form/LoginRegisterForm"), {
  ssr: false,
  loading: () => <div className="p-8 text-center">Loading...</div>
});
const OTPForm = dynamic(() => import("../component_form/OTPForm"), {
  ssr: false,
  loading: () => <div className="p-8 text-center">Loading...</div>
});
const ProfileMenu = dynamic(() => import("../component_unique/ProfileIconMenu"), {
  ssr: false,
  loading: () => <div className="p-4 text-center">Loading...</div>
});
const TAMIL_MONTHS_HEADER = ['ஜனவரி', 'பிப்ரவரி', 'மார்ச்', 'ஏப்ரல்', 'மே', 'ஜூன்', 'ஜூலை', 'ஆகஸ்ட்', 'செப்டம்பர்', 'அக்டோபர்', 'நவம்பர்', 'டிசம்பர்'];
function formatNow() {
  const d = new Date();
  const day = d.getDate();
  const month = TAMIL_MONTHS_HEADER[d.getMonth()];
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("ta-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
  return `${day} ${month} ${year}, ${time}`;
}
function formatNowMobile() {
  const d = new Date();
  const day = d.getDate();
  const month = TAMIL_MONTHS_HEADER[d.getMonth()];
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("ta-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
  return `${day} ${month}, ${year} ${time}`;
}

// Hierarchical menu item type
interface HierarchicalMenuItem extends MenuGroupItem {
  children?: HierarchicalMenuItem[];
}

// Build hierarchical menu from flat items
function buildMenuHierarchy(items: MenuGroupItem[]): HierarchicalMenuItem[] {
  const itemMap = new Map<number, HierarchicalMenuItem>();
  const rootItems: HierarchicalMenuItem[] = [];

  // First pass: create all items with children array
  items.forEach(item => {
    if (!item.title || !item.id) return;
    itemMap.set(item.id, {
      ...item,
      children: []
    });
  });

  // Second pass: build hierarchy
  items.forEach(item => {
    if (!item.title || !item.id) return;
    const menuItem = itemMap.get(item.id);
    if (!menuItem) return;
    if (item["parent-id"] && itemMap.has(item["parent-id"])) {
      const parent = itemMap.get(item["parent-id"]);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(menuItem);
      }
    } else {
      rootItems.push(menuItem);
    }
  });

  // Recursively remove empty children arrays
  const cleanEmptyChildren = (menuItems: HierarchicalMenuItem[]) => {
    menuItems.forEach(item => {
      if (item.children && item.children.length === 0) {
        delete item.children;
      } else if (item.children) {
        cleanEmptyChildren(item.children);
      }
    });
  };
  cleanEmptyChildren(rootItems);
  return rootItems;
}
interface HeaderProps {
  trendingTags?: string[];
  serverMenuData?: {
    "menu-groups"?: Record<string, any>;
  } | null;
}

// Helper to initialize menu items from server data (used for SSR)
function initMenuItemsFromServerData(serverMenuData: HeaderProps['serverMenuData']): HierarchicalMenuItem[] {
  if (!serverMenuData?.["menu-groups"]) return [];
  const items = serverMenuData["menu-groups"]?.default?.items ?? [];
  const cleaned = items.filter((it: any) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));
  return buildMenuHierarchy(cleaned);
}

// Helper to extract mega-menu items from server data (for mobile hamburger menu)
function initMegaMenuItemsFromServerData(serverMenuData: HeaderProps['serverMenuData']): HierarchicalMenuItem[] {
  if (!serverMenuData?.["menu-groups"]) return [];
  const groups = serverMenuData["menu-groups"];
  const megaGroupByName = Object.values(groups).find((g: any) => g?.name === "Mega Menu");
  const megaItems = (megaGroupByName as any)?.items ?? groups?.["mega-menu"]?.items ?? [];
  const cleaned = megaItems.filter((it: any) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link || it?.["section-name"]));
  return buildMenuHierarchy(cleaned);
}
function HeaderComponent({
  trendingTags = [],
  serverMenuData
}: HeaderProps) {
  const [openMenu, setOpenMenu] = useState(false);
  const [menuSource, setMenuSource] = useState<"top" | "bottom" | "compact">("top");
  const [openSearch, setOpenSearch] = useState(false);
  const [openAuth, setOpenAuth] = useState(false);
  const [authPopup, setAuthPopup] = useState<"login" | "otp" | "profile">("login");
  const [profileMenuPos, setProfileMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const {
    isDesktop,
    isMobile
  } = useDevice();
  // Initialize menu items directly from server data for instant render (no flash)
  const [desktopMenuItems, setDesktopMenuItems] = useState<HierarchicalMenuItem[]>(() => initMenuItemsFromServerData(serverMenuData));
  const [mobileMegaMenuItems, setMobileMegaMenuItems] = useState<HierarchicalMenuItem[]>(() => initMegaMenuItemsFromServerData(serverMenuData));
  const [mobileMenuExpandedId, setMobileMenuExpandedId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [mobileNavDropdown, setMobileNavDropdown] = useState<number | null>(null);
  const [mobileDropdownPosition, setMobileDropdownPosition] = useState<number>(0);
  const [selectedLanguage, setSelectedLanguage] = useState("தமிழ்");
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();

  // Scroll tracking for sticky nav
  const [isNavFixed, setIsNavFixed] = useState(false);

  // Language options with codes and URLs
  const languageOptions = [{
    code: "en",
    label: "English",
    url: "https://www.newindianexpress.com"
  }, {
    code: "ta",
    label: "தமிழ்",
    url: "/"
  }, {
    code: "kn",
    label: "ಕನ್ನಡ",
    url: "https://www.kannadaprabha.com/"
  }, {
    code: "ml",
    label: "മലയാളം",
    url: "https://www.samakalikamalayalam.com/"
  }];
  // const { theme, setTheme, resolvedTheme } = useTheme();
  const {
    isAuthenticated,
    resetAuthState
  } = usePublicAuth();
  const [currentTime, setCurrentTime] = useState<string>('');
  const [currentTimeMobile, setCurrentTimeMobile] = useState<string>('');
  const [mounted, setMounted] = useState(false);
  const userButtonRef = useRef<HTMLButtonElement | null>(null);
  const userButtonRefMobile = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const megaMenuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const mobileNavDropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Close menu/search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside header and mega menu
      if (openMenu) {
        const isOutsideHeader = headerRef.current && !headerRef.current.contains(target);
        const isOutsideMegaMenu = megaMenuRef.current && !megaMenuRef.current.contains(target);
        if (isOutsideHeader && isOutsideMegaMenu) {
          setOpenMenu(false);
        }
      }

      // Check if click is outside header and search
      if (openSearch) {
        const isOutsideHeader_0 = headerRef.current && !headerRef.current.contains(target);
        const isOutsideSearch = searchRef.current && !searchRef.current.contains(target);
        if (isOutsideHeader_0 && isOutsideSearch) {
          setOpenSearch(false);
        }
      }

      // Check if click is outside language dropdown
      if (languageDropdownOpen) {
        const isOutsideDropdown = languageDropdownRef.current && !languageDropdownRef.current.contains(target);
        if (isOutsideDropdown) {
          setLanguageDropdownOpen(false);
        }
      }

      // Check if click is outside mobile nav dropdown
      if (mobileNavDropdown !== null) {
        const isOutsideMobileDropdown = mobileNavDropdownRef.current && !mobileNavDropdownRef.current.contains(target);
        if (isOutsideMobileDropdown) {
          setMobileNavDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu, openSearch, languageDropdownOpen, mobileNavDropdown]);

  // Update time only on client to avoid hydration mismatch
  useEffect(() => {
    setCurrentTime(formatNow());
    setCurrentTimeMobile(formatNowMobile());

    // Update time every minute
    const interval = setInterval(() => {
      setCurrentTime(formatNow());
      setCurrentTimeMobile(formatNowMobile());
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const toggleSearch = useCallback(() => {
    setOpenSearch(prev => {
      if (!prev) {
        setOpenMenu(false);
        setMobileNavDropdown(null);
      }
      return !prev;
    });
  }, []);

  // const toggleTheme = useCallback(() => {
  //   setTheme(theme === 'dark' ? 'light' : 'dark');
  // }, [theme, setTheme]);

  const isActiveHref = (href: string): boolean => {
    const cleanHref = href.split("?")[0] || "/";
    const normalize = (p: string) => p.length > 1 ? p.replace(/\/+$/, "") : p;
    const a = normalize(pathname || "/");
    const b = normalize(cleanHref);
    return a === b;
  };
  const closeSearch = () => {
    setOpenSearch(false);
  };
  const openAuthModal = () => {
    const nextPopup = isAuthenticated ? "profile" : "login";
    setAuthPopup(nextPopup);
    const buttonEl = isDesktop ? userButtonRef.current : userButtonRefMobile.current;
    if (nextPopup === "profile" && buttonEl) {
      const rect = buttonEl.getBoundingClientRect();
      const desiredWidth = 260;
      const width = Math.min(desiredWidth, Math.max(180, window.innerWidth - 20));
      const left = Math.max(10, Math.min(rect.right - width, window.innerWidth - width - 10));
      const top = Math.min(rect.bottom + 8, window.innerHeight - 10);
      setProfileMenuPos({
        top,
        left
      });
    } else {
      setProfileMenuPos(null);
    }
    setOpenAuth(true);
    setOpenMenu(false);
    setOpenSearch(false);
  };
  const goToOtp = () => {
    setAuthPopup("otp");
    setProfileMenuPos(null);
    setOpenAuth(true);
  };
  const closeAuthModal = () => {
    setOpenAuth(false);
    resetAuthState();
  };
  const handleAuthSuccess = () => {
    setOpenAuth(false);
    resetAuthState();
  };

  // Close menus on route change
  useEffect(() => {
    setOpenMenu(false);
    setOpenSearch(false);
    setActiveDropdown(null);
    setMobileNavDropdown(null);
  }, [pathname]);

  // Fallback: Fetch client-side only if no menu items (server data wasn't available)
  useEffect(() => {
    // Skip if we already have menu items from server data
    if (desktopMenuItems.length > 0) {
      return;
    }

    // Fallback: Fetch client-side
    let cancelled = false;
    (async () => {
      const data = await getMenuGroups();
      const items = data?.["menu-groups"]?.default?.items ?? [];
      const cleaned = items.filter(it => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));
      const hierarchicalItems = buildMenuHierarchy(cleaned);
      if (!cancelled && hierarchicalItems.length > 0) {
        setDesktopMenuItems(hierarchicalItems);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // Only run once on mount

  // Fallback: Fetch mega-menu items client-side if not available from server
  useEffect(() => {
    if (mobileMegaMenuItems.length > 0) return;
    let cancelled_0 = false;
    (async () => {
      const data_0 = await getMenuGroups();
      const groups = data_0?.["menu-groups"];
      if (!groups || cancelled_0) return;
      const megaGroupByName = Object.values(groups).find((g: any) => g?.name === "Mega Menu");
      const megaItems = (megaGroupByName as any)?.items ?? groups?.["mega-menu"]?.items ?? [];
      const cleaned_0 = megaItems.filter((it_0: any) => Boolean(it_0?.title) && Boolean(it_0?.url || it_0?.data?.link || it_0?.["section-name"]));
      const items_0 = buildMenuHierarchy(cleaned_0);
      if (!cancelled_0 && items_0.length > 0) {
        setMobileMegaMenuItems(items_0);
      }
    })();
    return () => {
      cancelled_0 = true;
    };
  }, []);
  const resolveMenuHref = (item: MenuGroupItem): {
    href: string;
    external: boolean;
  } | null => {
    const raw = item?.url || item?.data?.link;
    if (!raw) return null;

    // Keep full path (e.g., /cinema/cinema-news) instead of just last segment
    const toFullPath = (pathnameValue: string): string => {
      const cleaned_1 = (pathnameValue || "/").split("?")[0].replace(/\/+$/, "");
      return cleaned_1 || "/";
    };
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "https://www.dinamani.com";
      const parsed = new URL(raw, base);
      const isDinamani = parsed.hostname === "www.dinamani.com" || parsed.hostname === "dinamani.com";
      if (isDinamani) {
        return {
          href: toFullPath(parsed.pathname || "/"),
          external: false
        };
      }
      return {
        href: raw,
        external: true
      };
    } catch {
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return {
          href: raw,
          external: true
        };
      }
      return {
        href: toFullPath(raw),
        external: false
      };
    }
  };
  useEffect(() => {
    if (!openAuth) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [openAuth]);

  // Scroll listener for fixed nav
  useEffect(() => {
    const handleScroll = () => {
      setIsNavFixed(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <>
      <header ref={headerRef} className="w-full bg-F8F8F8 shadow-sm">
        {/* TOP STRIP */}

        <div className="relative border-t border-b border-DCDCDC lg:border-DCDCDC">
          <div className="xl:max-w-[1245px] w-full mx-auto">
            {/* center date/time */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="tracking-wide text-14-english-600 md:text-12-english-600 text-1B1B1B">
                <span className="lg:hidden" suppressHydrationWarning>{currentTimeMobile}</span>
                <span className="hidden lg:inline" suppressHydrationWarning>{currentTime}</span>
              </div>

            </div>
            {/* left and right content */}
            <div className="flex items-center justify-start lg:justify-between h-full relative z-10">
              {/* Mobile layout - 2 icons left, 2 icons right */}
              <div className="lg:hidden flex items-center justify-between w-full border-DCDCDC text-h2-english">
                {/* Left side icons - mobile only */}
                <div className="flex items-center">
                  <button onClick={() => {
                  setMenuSource("top");
                  setOpenMenu(!openMenu);
                  setOpenSearch(false);
                  setMobileNavDropdown(null);
                }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
                  backgroundColor: openMenu && menuSource === "top" ? '#012061' : 'transparent'
                }}>
                    <span className={`text-[15px] ${openMenu && menuSource === "top" ? 'text-white' : 'text-1B1B1B'}`}>
                      {openMenu && menuSource === "top" ? <FaTimes /> : <HiMenu />}
                    </span>
                  </button>

                  {/* Search icon - left side mobile */}
                  <button className="p-2 cursor-pointer" style={{
                  backgroundColor: openSearch ? '#012061' : 'transparent'
                }} onClick={toggleSearch} aria-label={openSearch ? "Close search" : "Open search"}>
                    <span className={`text-[13px] ${openSearch ? 'text-white' : 'text-1B1B1B'}`}>
                      {openSearch ? <FaTimes /> : <FaSearch />}
                    </span>
                  </button>
                </div>

                {/* Right side icons - mobile only */}
                <div className="flex items-center">
                  {/* Bookmark icon */}
                  <Link href="/bookmark" className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" aria-label="Bookmarks">
                    <FaBookmark aria-label="Bookmarks" />
                  </Link>

                  {/* User icon */}
                  <button className="inline-block text-[16px] p-2 text-1B1B1B cursor-pointer" onClick={openAuthModal} aria-label="Open login or register" ref={userButtonRefMobile}>
                    <FaUser aria-label="User" />
                  </button>
                </div>
              </div>

              {/* Desktop layout - original horizontal row */}
              <div className="hidden lg:flex items-center border-DCDCDC text-h2-english">
                <button onClick={() => {
                setMenuSource("top");
                setOpenMenu(!openMenu);
                setOpenSearch(false);
                setMobileNavDropdown(null);
                setActiveDropdown(null);
              }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
                backgroundColor: openMenu && menuSource === "top" ? '#012061' : 'transparent'
              }}>
                  <span className={`text-[18px] ${openMenu && menuSource === "top" ? 'text-white' : 'text-1B1B1B'}`}>
                    {openMenu && menuSource === "top" ? <FaTimes /> : <HiMenu />}
                  </span>
                </button>

                {/* Search icon - visible on all screens */}
                <button className="p-2 cursor-pointer" style={{
                backgroundColor: openSearch ? '#012061' : 'transparent'
              }} onClick={toggleSearch} aria-label={openSearch ? "Close search" : "Open search"}>
                  <span className={`text-[16px] ${openSearch ? 'text-white' : 'text-1B1B1B'}`}>
                    {openSearch ? <FaTimes /> : <FaSearch />}
                  </span>
                </button>

                {/* Theme toggle icon */}
                {/* <button
                  className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors"
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                 >
                  {theme === 'dark' ? <FaSun aria-label="Switch to light mode" /> : <FaMoon aria-label="Switch to dark mode" />}
                 </button> */}

                {/* Bookmark icon */}
                <Link href="/bookmark" className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" aria-label="Bookmarks">
                  <FaBookmark aria-label="Bookmarks" />
                </Link>

                {/* User icon */}
                <button className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" onClick={openAuthModal} aria-label="Open login or register" ref={userButtonRef}>
                  <FaUser aria-label="User" />
                </button>
                <div>
                  {/* language selector */}
                  <div className="hidden lg:block relative" ref={languageDropdownRef}>
                    <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-1.5 px-3 py-1.5 text-13-english-600 text-1B1B1B hover:text-012061 transition-colors cursor-pointer border border-DCDCDC bg-white hover:border-012061">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <span>{selectedLanguage}</span>
                      <FaChevronDown className={`text-[10px] transition-transform duration-200 ${languageDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown menu */}
                    {languageDropdownOpen && <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-DCDCDC shadow-lg rounded-md min-w-[160px] py-1 animate-in fade-in slide-in-from-top-2 duration-200">
                        {languageOptions.map(lang => {
                      const isTamil = lang.code === "ta";
                      return <button key={lang.code} onClick={() => {
                        setLanguageDropdownOpen(false);
                        // Only open new tab for non-Tamil languages
                        // Tamil is always selected, don't change anything
                        if (!isTamil) {
                          window.open(lang.url, "_blank");
                        }
                      }} className={`w-full text-left px-4 py-2 text-13-english-500 transition-colors cursor-pointer flex items-center gap-2 ${isTamil ? "bg-012061/10 text-012061 font-semibold" : "text-gray-700 hover:bg-gray-100 hover:text-012061"}`}>
                              {isTamil && <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>}
                              <span className={isTamil ? "" : "ml-5"}>
                                {lang.label}
                              </span>
                            </button>;
                    })}
                      </div>}
                  </div>
                </div>
              </div>


              {/* hide social icons on mobile */}
              <div className="p-1 hidden lg:block"><SocialIcons useGrid={true} /></div>
            </div>
          </div>

        </div>

        {/* MAIN HEADER AREA */}
        <div className="relative bg-F8F8F8">
          <div className="xl:max-w-[1245px] w-full mx-auto p-1">
            <div className="flex items-center justify-between h-[46px] lg:h-[65px]">
              {/* Left brand (Indian Express) - desktop only */}
              <div className="shrink-0 hidden lg:block">
                <Link href="/">
                  {/* Above-the-fold header logo — eager load so the flex
                      layout doesn't reflow when w-auto recomputes from the
                      image's intrinsic ratio after a lazy fetch. */}
                  <Image src="https://media.dinamani.com/dinamani/2026-02-27/125ubsfr/indianexpresslogo.png" alt="The New Indian Express Group" width={320} height={60} className="h-[60px] w-[320px] object-contain" loading="eager" fetchPriority="high" />
                </Link>
              </div>

              {/* Right actions */}
              <div className="hidden lg:flex gap-2 items-end">
                <Link href="/newsletter" className="inline-flex items-center gap-2 bg-ECEEF3 text-14-english-700 h-[36px] w-[152px] justify-center text-012061">
                  <Image src="https://media.dinamani.com/dinamani/2026-02-27/0o5b196u/crownicon.png" alt="Crown" width={20} height={20} className="" loading="lazy" />
                  செய்தி மடல்
                </Link>
                <Link href="https://epaper.dinamani.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-ECEEF3 text-14-english-700 h-[36px] w-[152px] justify-center text-012061">
                  <Image src="https://media.dinamani.com/dinamani/2026-02-27/36zobb67/papericon.png" alt="Paper" width={20} height={20} className="" loading="lazy" />
                  இ-பேப்பர்
                </Link>
              </div>
            </div>

            {/* centered Dinamani logo */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Link href="/" prefetch className="pointer-events-auto">
                <Image src="https://media.dinamani.com/dinamani/2026-02-27/y9s7ztw3/dinamanilogo.png" alt="Dinamani" width={150} height={60} className="w-[140px] h-[40px] object-contain lg:w-[150px] lg:h-[60px]" loading="eager" sizes="(max-width: 1024px) 140px, 220px" />
              </Link>
            </div>
          </div>
        </div>

        {/* NAV BAR - RESPONSIVE */}
        <nav className={`border-t border-b-[3px] border-012061 border-x-0 transition-all duration-300 ${isNavFixed ? 'fixed top-0 left-0 right-0 z-[9998] shadow-md bg-F8F8F8' : ''}`}>
          <div className="xl:max-w-[1245px] w-full mx-auto">
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center justify-between gap-5 text-14-tamil-600 text-020202">
              {desktopMenuItems.slice(0, 9).map(item_0 => {
              const resolved = resolveMenuHref(item_0);
              if (!resolved) return null;
              const active = !resolved.external && isActiveHref(resolved.href);
              const hasChildren = item_0.children && item_0.children.length > 0;
              const isElection2026 = item_0.title === "தேர்தல் 2026";
              const baseClassName = isElection2026 ? "cursor-pointer font-tamil font-bold" : active ? "cursor-pointer font-tamil text-[#215EF9] font-bold" : "cursor-pointer font-tamil hover:text-012061";
              const electionStyle = isElection2026 ? {
                color: '#e5484d'
              } : undefined;

              // Item with submenu
              if (hasChildren) {
                const isDropdownOpen = activeDropdown === item_0.id;
                return <div key={`${item_0.title}-${resolved.href}`} className="relative" onMouseEnter={() => {
                  if (openMenu) setOpenMenu(false);
                  setActiveDropdown(item_0.id);
                }} onMouseLeave={() => setActiveDropdown(null)}>
                      <div className="flex items-center gap-1 py-2 cursor-pointer">
                        {resolved.external ? <Link href={resolved.href} target="_blank" rel="noreferrer" className={baseClassName} style={electionStyle}>
                            {item_0.title}
                          </Link> : <Link href={resolved.href} className={baseClassName} style={electionStyle}>
                            {item_0.title}
                          </Link>}
                        <FaChevronDown className={`text-[10px] text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-012061' : ''}`} />
                      </div>
                      {/* Dropdown */}
                      {isDropdownOpen && <div className="absolute top-full left-0 pt-1 z-50">
                          <div className="bg-white border border-DCDCDC shadow-lg rounded-md min-w-[200px]">
                            <ul className="py-2">
                              {item_0.children!.map(child => {
                          const childResolved = resolveMenuHref(child);
                          if (!childResolved) return null;
                          return <li key={`${child.title}-${childResolved.href}`}>
                                    {childResolved.external ? <Link href={childResolved.href} target="_blank" rel="noreferrer" className="block px-4 py-2 text-13-tamil-500 text-gray-700 hover:bg-gray-100 hover:text-012061 transition-colors">
                                        {child.title}
                                      </Link> : <Link href={childResolved.href} className="block px-4 py-2 text-13-tamil-500 text-gray-700 hover:bg-gray-100 hover:text-012061 transition-colors">
                                        {child.title}
                                      </Link>}
                                  </li>;
                        })}
                            </ul>
                          </div>
                        </div>}
                    </div>;
              }

              // Item without submenu
              if (resolved.external) {
                return <Link key={`${item_0.title}-${resolved.href}`} href={resolved.href} target="_blank" rel="noreferrer" className={baseClassName} style={electionStyle} onClick={() => setOpenMenu(false)}>
                      {item_0.title}
                    </Link>;
              }
              return <Link key={`${item_0.title}-${resolved.href}`} href={resolved.href} className={baseClassName} style={electionStyle} onClick={() => setOpenMenu(false)}>
                    {item_0.title}
                  </Link>;
            })}
              {/* Menu button at end of nav - only show when menu items are loaded */}
              {desktopMenuItems.length > 0 && <button onClick={() => {
              setMenuSource("bottom");
              setOpenMenu(!openMenu);
              setOpenSearch(false);
              setMobileNavDropdown(null);
              setActiveDropdown(null);
            }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
              backgroundColor: openMenu && menuSource === "bottom" ? '#012061' : 'transparent'
            }}>
                  <span className={`text-[15px] flex items-center justify-center ${openMenu && menuSource === "bottom" ? 'text-white' : 'text-1B1B1B'}`}>
                    {openMenu && menuSource === "bottom" ? <FaTimes /> : <FaEllipsisH />}
                  </span>
                </button>}
            </div>
            {/* Mobile Scrollable Menu */}
            <div className="lg:hidden relative" style={{
            backgroundColor: '#012061'
          }} ref={mobileNavDropdownRef}>
              {/* Fixed menu button on left side - only show when menu items are loaded */}
              {desktopMenuItems.length > 0 && <div className="absolute left-0 top-0 h-full flex items-center border-r border-white/20 z-10" style={{
              backgroundColor: '#012061'
            }}>
                  <button onClick={() => {
                setMenuSource("bottom");
                setOpenMenu(!openMenu);
                setOpenSearch(false);
                setMobileNavDropdown(null);
              }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-3">
                    <span className="text-[15px] text-white flex items-center justify-center">
                      {openMenu && menuSource === "bottom" ? <FaTimes size={15} /> : <FaEllipsisH size={15} />}
                    </span>
                  </button>
                </div>}

              <div className="overflow-x-auto scrollbar-hide">
                <div className="flex gap-2 text-12-tamil-500 text-FFFFFF">
                  {/* Spacer for menu button - only show when menu items are loaded */}
                  {desktopMenuItems.length > 0 && <div className="w-10 shrink-0" />}
                  {/* Menu items */}
                  {desktopMenuItems.slice(0, 9).map(item_1 => {
                  const resolved_0 = resolveMenuHref(item_1);
                  if (!resolved_0) return null;
                  const baseClass = "font-tamil font-semibold whitespace-nowrap p-2";
                  const active_0 = !resolved_0.external && isActiveHref(resolved_0.href);
                  const hasChildren_0 = item_1.children && item_1.children.length > 0;
                  const isDropdownOpen_0 = mobileNavDropdown === item_1.id;
                  const isElection2026_0 = item_1.title === "தேர்தல் 2026";
                  const className = active_0 ? `${baseClass} lg:border-b-4 border-b-2 border-FFFFFF pb-1` : baseClass;
                  const electionStyle_0 = isElection2026_0 ? {
                    color: '#e5484d'
                  } : undefined;

                  // Item with children - show dropdown on click
                  if (hasChildren_0) {
                    return <button key={`${item_1.title}-${resolved_0.href}`} onClick={e => {
                      const button = e.currentTarget;
                      const rect_0 = button.getBoundingClientRect();
                      setMobileDropdownPosition(rect_0.left);
                      setMobileNavDropdown(isDropdownOpen_0 ? null : item_1.id);
                      setOpenMenu(false);
                    }} className={`${className} flex items-center gap-1`} style={electionStyle_0}>
                          {item_1.title}
                          <FaChevronDown className={`text-[8px] transition-transform duration-200 ${isDropdownOpen_0 ? 'rotate-180' : ''}`} />
                        </button>;
                  }

                  // Item without children - regular link
                  if (resolved_0.external) {
                    return <Link key={`${item_1.title}-${resolved_0.href}`} href={resolved_0.href} target="_blank" rel="noreferrer" className={className} onClick={() => setOpenMenu(false)} style={electionStyle_0}>
                          {item_1.title}
                        </Link>;
                  }
                  return <Link key={`${item_1.title}-${resolved_0.href}`} href={resolved_0.href} className={className} onClick={() => setOpenMenu(false)} style={electionStyle_0}>
                        {item_1.title}
                      </Link>;
                })}
                  {/* Spacer to prevent overlap with absolute theme toggle */}
                  <div className="w-16 shrink-0" />
                </div>
              </div>

              {/* Mobile dropdown - rendered outside scrollable container */}
              {mobileNavDropdown !== null && (() => {
              const activeItem = desktopMenuItems.find(item_2 => item_2.id === mobileNavDropdown);
              if (!activeItem || !activeItem.children) return null;
              const resolved_1 = resolveMenuHref(activeItem);
              if (!resolved_1) return null;

              // Calculate left position, ensure dropdown doesn't overflow screen
              const dropdownWidth = 200;
              const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
              let leftPos = mobileDropdownPosition;
              // Prevent overflow on right side
              if (leftPos + dropdownWidth > screenWidth) {
                leftPos = screenWidth - dropdownWidth - 8;
              }
              // Prevent negative left
              if (leftPos < 8) {
                leftPos = 8;
              }
              return <div className="absolute top-full z-50 bg-012061 border border-white/20 shadow-lg min-w-[220px] py-2" style={{
                left: `${leftPos}px`
              }}>
                    {/* Parent item as first option */}
                    <Link href={resolved_1.href} className="block px-5 py-3 text-14-tamil-600 text-white hover:bg-white/10 transition-colors border-b border-white/10 font-semibold leading-relaxed" onClick={() => setMobileNavDropdown(null)}>
                      {activeItem.title}
                    </Link>
                    {/* Child items */}
                    {activeItem.children.map(child_0 => {
                  const childResolved_0 = resolveMenuHref(child_0);
                  if (!childResolved_0) return null;
                  return <Link key={`${child_0.title}-${childResolved_0.href}`} href={childResolved_0.href} target={childResolved_0.external ? "_blank" : undefined} rel={childResolved_0.external ? "noreferrer" : undefined} className="block px-5 py-3 text-13-tamil-500 text-white hover:bg-white/10 transition-colors leading-relaxed" onClick={() => setMobileNavDropdown(null)}>
                          {child_0.title}
                        </Link>;
                })}
                  </div>;
            })()}

              {/* Fixed theme toggle icon on right side */}
              {/* <div
                className="absolute right-0 top-0 h-full flex items-center px-4 border-l border-white/20"
                style={{ backgroundColor: '#012061' }}
               >
                <button
                  className="text-white hover:text-gray-200 transition-colors"
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? <FaSun aria-label="Switch to light mode" /> : <FaMoon aria-label="Switch to dark mode" />}
                </button>
               </div> */}
            </div>
          </div>

        </nav>
      </header>

      {/* MEGA MENU OVERLAY */}
      {openMenu && (!isDesktop && menuSource === "bottom" ? (/* Mobile 3-dots: show mega-menu API items */
    <div ref={megaMenuRef} className="fixed inset-0 z-[9999]" style={{
      backgroundColor: '#FFFFFF'
    }}>
            <div className="h-full overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 z-10 p-4 border-b border-DCDCDC" style={{
          backgroundColor: '#F8F8F8'
        }}>
                <div className="flex items-center justify-between">
                  <Image src="/dinamani/dinamani_logo.avif" alt="Dinamani" width={120} height={40} className="w-auto h-10" loading="lazy" />
                  <button onClick={() => setOpenMenu(false)} className="p-2 rounded-full transition-colors bg-ECEEF3 hover:bg-gray-200" aria-label="Close menu">
                    <FaTimes className="text-1B1B1B" size={16} />
                  </button>
                </div>
              </div>
              {/* Mega menu items */}
              <div className="p-4">
                <ul className="space-y-2">
                  {mobileMegaMenuItems.map(item_3 => {
              const resolved_2 = resolveMenuHref(item_3);
              if (!resolved_2) return null;
              const hasChildren_1 = item_3.children && item_3.children.length > 0;
              const isExpanded = mobileMenuExpandedId === item_3.id;
              return <li key={item_3.id} className="transition-colors rounded-lg bg-F8F8F8 hover:bg-ECEEF3">
                        <div className="p-4 flex items-center justify-between">
                          <div className="flex-1">
                            {resolved_2.external ? <a href={resolved_2.href} target="_blank" rel="noreferrer" className="text-14-tamil-600 text-012061 font-semibold block" onClick={() => setOpenMenu(false)}>
                                {item_3.title}
                              </a> : <Link href={resolved_2.href} className="text-14-tamil-600 text-012061 font-semibold block" onClick={() => setOpenMenu(false)}>
                                {item_3.title}
                              </Link>}
                          </div>
                          {hasChildren_1 && <button onClick={() => setMobileMenuExpandedId(isExpanded ? null : item_3.id)} className="p-2 hover:bg-gray-200 rounded transition-colors cursor-pointer" aria-label={isExpanded ? "Collapse submenu" : "Expand submenu"}>
                              <FaChevronDown className={`text-[12px] text-012061 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>}
                        </div>
                        {hasChildren_1 && isExpanded && <ul className="pl-6 pb-3 space-y-1 border-l-2 border-gray-300 ml-4">
                            {item_3.children!.map(child_1 => {
                    const childResolved_1 = resolveMenuHref(child_1);
                    if (!childResolved_1) return null;
                    return <li key={child_1.id} className="rounded-lg hover:bg-gray-200">
                                  <div className="p-2">
                                    {childResolved_1.external ? <a href={childResolved_1.href} target="_blank" rel="noreferrer" className="text-13-tamil-400 text-gray-600 hover:text-1B1B1B block" onClick={() => setOpenMenu(false)}>
                                        {child_1.title}
                                      </a> : <Link href={childResolved_1.href} className="text-13-tamil-400 text-gray-600 hover:text-1B1B1B block" onClick={() => setOpenMenu(false)}>
                                        {child_1.title}
                                      </Link>}
                                  </div>
                                </li>;
                  })}
                          </ul>}
                      </li>;
            })}
                </ul>
              </div>
            </div>
          </div>) : (/* Hamburger (top) + Desktop: use existing MegaMenu component (sidebar-menu API) */
    <div ref={megaMenuRef} className={isNavFixed ? 'fixed top-[41px] left-0 right-0 z-[9997]' : ''}>
            <MegaMenu onClose={() => setOpenMenu(false)} isDesktop={isDesktop} menuSource={menuSource} serverMenuData={serverMenuData} isNavFixed={isNavFixed} />
          </div>))}

      {/* SEARCH OVERLAY */}
      {openSearch && <div ref={searchRef} className={isDesktop ? "relative inset-0 z-[9]" : "fixed inset-0 z-[9999] bg-FFFFFF"}>
          <div className={isDesktop ? "absolute top-4 left-4 right-4 mx-auto max-w-6xl" : "absolute inset-0"} style={isDesktop ? {
        marginTop: '-8.5rem'
      } : {}}>
            <div className={isDesktop ? "bg-FFFFFF border border-DCDCDC shadow-lg" : "h-full overflow-y-auto relative"} style={isDesktop ? {
          maxHeight: '80vh',
          overflowY: 'auto'
        } : {}}>
              {/* Close button - matches MegaMenu style */}
              {!isDesktop && <button onClick={closeSearch} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" aria-label="Close search">
                  <FaTimes className="text-808080-700" size={20} />
                </button>}

              <div className={isDesktop ? "" : ""}>
                <SearchLayoutSection onClose={closeSearch} trendingTags={trendingTags} />
              </div>
            </div>
          </div>
        </div>}

      {openAuth && <div className="fixed inset-0 z-[9999]">
          <button className="absolute inset-0 bg-black/50" aria-label="Close login or register" onClick={closeAuthModal} />
          {authPopup === "profile" && profileMenuPos ? <div className="absolute" style={{
        top: profileMenuPos.top,
        left: profileMenuPos.left,
        width: "min(260px, calc(100vw - 20px))"
      }}>
              <div className="overflow-hidden shadow-lg border border-DCDCDC bg-FFFFFF">
                <ProfileMenu />
              </div>
            </div> : <div className="absolute inset-0 flex items-stretch justify-center p-[10px] sm:items-center sm:p-6 md:p-8 lg:p-12">
              <div className="relative w-full h-[calc(100dvh-20px)] overflow-x-hidden overflow-y-auto sm:h-auto sm:max-h-[92vh] sm:max-w-md lg:max-w-[537px] sm:shadow-lg">
                {authPopup === "login" ? <LoginRegisterForm onClose={closeAuthModal} onContinue={goToOtp} /> : authPopup === "otp" ? <OTPForm onClose={closeAuthModal} onSuccess={handleAuthSuccess} /> : <ProfileMenu />}
              </div>
            </div>}
        </div>}
    </>;
}

// Memoize to prevent unnecessary re-renders
export default memo(HeaderComponent);